import { SEO } from '../components';
import { Header, Footer } from '../components/sections';
import { getCanonicalUrl, buildHreflangTags } from '../lib/siteUrl';

const CONTACT_EMAIL = 'tietosuoja@identio.fi';

export default function PrivacyPolicyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy | Identio"
        description="Identio Privacy Policy – data we collect, how we use it, cookies and analytics, and your rights."
        url={getCanonicalUrl('/privacy-policy')}
        alternates={buildHreflangTags('/tietosuojaseloste', '/privacy-policy')}
      />
      <Header />
      <main id="main-content" className="min-h-screen bg-white" tabIndex={-1}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10">
            Identio Privacy Policy
          </h1>

          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-ul:text-gray-600 prose-li:my-1 [&_ul]:list-none">
            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">1. Identio</h2>
              <p>
                Identio Oy (&quot;Identio&quot;, &quot;we&quot;, &quot;us&quot;) Linnankatu 13a B20, 20100 Turku, Finland
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">2. Contact</h2>
              <p>
                Questions about data protection at Identio? Contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">
                  {CONTACT_EMAIL}
                </a>{' '}
                and we&apos;ll get back to you.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">3. What is this notice and why do we have it?</h2>
              <p>
                We collect personal information about recruitment candidates, current and potential clients, partners, and individuals who wish to receive our newsletter or other communication.
              </p>
              <p>
                We collect this data so that we can respond to business requests, send newsletters to our subscribers, and handle job applications sent to us. Our website also uses analytics to collect information about how it&apos;s used. This information is used in the development of user experience.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">4. What data do we collect and why?</h2>

              <h3 className="text-lg md:text-xl mt-6 mb-3">Recruitment candidates</h3>
              <p>
                We&apos;ll collect the following information about you (you may have given this information on our website, or we&apos;ve got it during the recruitment process):
              </p>
              <ul className="space-y-1 my-4">
                <li>Your name and contact information</li>
                <li>Professional skills and interests</li>
                <li>Your LinkedIn profile</li>
                <li>CV and other files/information that you personally send us</li>
                <li>Data that&apos;s relevant to process your application. Information we get during discussions, interviews and other communication between you and Identio.</li>
              </ul>
              <p>
                We use this data only to process your application and communicate with you during the recruitment process. We do not share any of your information with anyone else, unless it is expressly required by applicable laws or requests by public authorities. We will remove personal information collected during the recruitment process within two (2) years of the end of the last contact in the process.
              </p>
              <p>
                In our recruitment process, we use secure AI-assisted features (such as tools provided by our recruitment system) to streamline the handling of application materials. AI may be used, for example, to summarize the applications and CV you submit and to create written summaries to support our recruitment team.
              </p>
              <p>
                AI serves only as an assisting tool: it does not make independent decisions, does not score or rank candidates, and is not used to automatically screen out applicants. All assessments and decisions in the recruitment process are always made by a human, and the original materials you submit are always available to our recruitment team in full and can be reviewed by them.
              </p>

              <h3 className="text-lg md:text-xl mt-6 mb-3">Clients, partners, other contacts</h3>
              <p>
                Whether you are an existing Identio client, subscribed to our newsletter or requested a contact regarding business opportunities, in order to be able to keep in touch with you, we collect and process the following data:
              </p>
              <ul className="space-y-1 my-4">
                <li>Name, title and contact information</li>
                <li>Information about your organization and your position in it</li>
                <li>All communication with Identio and the necessary information and notes concerning such contacts between you and Identio.</li>
                <li>Information on marketing, newsletter, event or invitation concerning you.</li>
                <li>Data created based on the above described data we already have.</li>
              </ul>

              <h3 className="text-lg md:text-xl mt-6 mb-3">We use this data for</h3>
              <ul className="space-y-1 my-4">
                <li>Managing the business relationship between you and Identio</li>
                <li>Processing requests sent to us, providing our services and pursuing the legitimate interests of Identio related to such request and services</li>
                <li>Promoting our services, communicating with you and sending newsletters you have subscribed to</li>
                <li>Planning, analyzing and developing our operations</li>
              </ul>
              <p>
                We don&apos;t share the information regarding you with any other parties outside of Identio, unless expressly required by applicable laws or public authorities. We may also share your data to our trusted partners if processing your request of our services requires us to do that.
              </p>
              <p>
                Identio has a legitimate interest to process the personal data listed above for the purposes described above. This interest is based on your connection with us and concerns for example your position in a company related to us or your direct request to us.
              </p>
              <p>
                We may also ask for your consent to the processing of the data (for example, if we want to save your job application for future career opportunities). In the situation described, you have the right to withdraw your consent to the storage of the data.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">5. Cookies and analytics</h2>
              <p>
                We use cookies and analytics on our website to improve user experience. Cookies are small text files that a browser saves on your device. They are used, for example, when a service wants to save your information while moving from one website to another. If you don&apos;t want to use cookies, you can change your own cookie settings in your browser at any time. Please notice that disabling cookies may impact how our website operates.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">6. How long do we keep your data?</h2>
              <p><strong>Job candidates:</strong></p>
              <p>
                We store the data collected during the application process for two (2) years from the date of last contact with you.
              </p>
              <p><strong>Clients and requests for new business or newsletters:</strong></p>
              <p>
                We store the data only as long as we consider necessary for the respective purpose. Since this period of time depends on the case, we go through the necessity of data regularly and remove data when we no longer need it.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">7. What are your rights?</h2>
              <p>
                You have the right to request access to, removing and correcting your personal information as held and processed by Identio, as well as to send us other requests based on processing the data. You also have the right to forbid us from sending newsletters or any other marketing messages or materials to you. Please send any requests concerning your data to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">
                  {CONTACT_EMAIL}
                </a>.
              </p>
              <p>
                Data protection laws are supervised by the Finnish Data Protection Ombudsman. If you have questions for the jurisdiction of the Data Protection Ombudsman, or if you wish to complain about processing personal data, you may contact the Data Protection Ombudsman&apos;s office at www.tietosuoja.fi.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">8. Information and data security</h2>
              <p>
                All personal data we collect is confidential. Your data can only be accessed by the Identio employees in case it&apos;s necessary for them to perform their work related to your data.
              </p>
              <p>
                The networks and devices of Identio and its partners are protected against accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access to personal information.
              </p>
              <p>
                If necessary, we may outsource the processing of personal data to an external service provider. In such situations, we enter into appropriate agreements with the service provider to make sure that your personal information is processed confidentially and in accordance with this Privacy statement and applicable laws.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl mt-8 mb-4">9. Changes to this Privacy policy</h2>
              <p>
                You will always find the most recent version of this notice on our website. We may update this notice when we improve our website or operations.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
